export const IDENTITY_SESSION_COOKIE = "qy_identity_session";
export const IDENTITY_SESSION_MAX_AGE_SECONDS = 12 * 60 * 60;

export type IdentityProvider = "cloudflare-access" | "google" | "local";

export type AuthenticatedUser = {
  displayName: string;
  email: string;
  fullName: string | null;
  provider: IdentityProvider;
};

type IdentitySessionPayload = {
  email: string;
  exp: number;
  iat: number;
  name: string | null;
  provider: "google";
};

const encoder = new TextEncoder();
const decoder = new TextDecoder();
const SESSION_VERSION = "v1";
const MAX_SESSION_TOKEN_LENGTH = 4096;

function bytesToBase64Url(bytes: Uint8Array) {
  let value = "";
  for (const byte of bytes) value += String.fromCharCode(byte);
  return btoa(value).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

function base64UrlToBytes(value: string) {
  const normalized = value.replace(/-/g, "+").replace(/_/g, "/");
  const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=");
  return Uint8Array.from(atob(padded), (character) => character.charCodeAt(0));
}

async function hmac(value: string, secret: string) {
  const key = await crypto.subtle.importKey(
    "raw",
    encoder.encode(secret),
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"],
  );
  return new Uint8Array(await crypto.subtle.sign("HMAC", key, encoder.encode(value)));
}

export function normalizeIdentityEmail(value: string) {
  return value.trim().toLowerCase();
}

export function isValidIdentityEmail(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizeIdentityEmail(value));
}

export function safeRelativeReturnPath(value: string | null | undefined) {
  if (!value || !value.startsWith("/") || value.startsWith("//")) return "/";
  let url: URL;
  try {
    url = new URL(value, "https://app.local");
  } catch {
    return "/";
  }
  if (url.origin !== "https://app.local" || url.pathname.startsWith("/api/auth/")) return "/";
  return `${url.pathname}${url.search}${url.hash}`;
}

export function constantTimeStringEqual(left: string, right: string) {
  const leftBytes = encoder.encode(left);
  const rightBytes = encoder.encode(right);
  if (leftBytes.length !== rightBytes.length) return false;
  let difference = 0;
  for (let index = 0; index < leftBytes.length; index += 1) {
    difference |= leftBytes[index] ^ rightBytes[index];
  }
  return difference === 0;
}

function normalizedDisplayName(value: string | null | undefined, email: string) {
  const trimmed = value?.trim().replace(/\s+/g, " ").slice(0, 120);
  return trimmed || email;
}

function validSessionSecret(secret: string | null | undefined) {
  const normalized = secret?.trim() ?? "";
  return normalized.length >= 32 ? normalized : null;
}

export async function createIdentitySessionToken(
  input: { email: string; fullName?: string | null },
  secret: string,
  nowMs = Date.now(),
) {
  const signingSecret = validSessionSecret(secret);
  const email = normalizeIdentityEmail(input.email);
  if (!signingSecret) throw new Error("身份会话密钥至少需要 32 个字符");
  if (!isValidIdentityEmail(email)) throw new Error("Google 身份缺少有效邮箱");
  const issuedAt = Math.floor(nowMs / 1000);
  const payload: IdentitySessionPayload = {
    email,
    exp: issuedAt + IDENTITY_SESSION_MAX_AGE_SECONDS,
    iat: issuedAt,
    name: input.fullName?.trim().slice(0, 120) || null,
    provider: "google",
  };
  const encodedPayload = bytesToBase64Url(encoder.encode(JSON.stringify(payload)));
  const signedValue = `${SESSION_VERSION}.${encodedPayload}`;
  const signature = bytesToBase64Url(await hmac(signedValue, signingSecret));
  return `${signedValue}.${signature}`;
}

export async function verifyIdentitySessionToken(
  token: string | null | undefined,
  secret: string | null | undefined,
  nowMs = Date.now(),
): Promise<AuthenticatedUser | null> {
  const signingSecret = validSessionSecret(secret);
  if (!token || !signingSecret || token.length > MAX_SESSION_TOKEN_LENGTH) return null;
  const [version, encodedPayload, encodedSignature, ...extra] = token.split(".");
  if (extra.length || version !== SESSION_VERSION || !encodedPayload || !encodedSignature) return null;

  let actualSignature: Uint8Array;
  let payload: IdentitySessionPayload;
  try {
    actualSignature = base64UrlToBytes(encodedSignature);
    payload = JSON.parse(decoder.decode(base64UrlToBytes(encodedPayload))) as IdentitySessionPayload;
  } catch {
    return null;
  }
  const expectedSignature = await hmac(`${version}.${encodedPayload}`, signingSecret);
  if (actualSignature.length !== expectedSignature.length) return null;
  let difference = 0;
  for (let index = 0; index < actualSignature.length; index += 1) {
    difference |= actualSignature[index] ^ expectedSignature[index];
  }
  if (difference !== 0) return null;

  const now = Math.floor(nowMs / 1000);
  const email = normalizeIdentityEmail(payload.email ?? "");
  if (
    payload.provider !== "google"
    || !isValidIdentityEmail(email)
    || !Number.isInteger(payload.iat)
    || !Number.isInteger(payload.exp)
    || payload.iat > now + 60
    || payload.exp <= now
    || payload.exp - payload.iat !== IDENTITY_SESSION_MAX_AGE_SECONDS
  ) return null;
  const fullName = payload.name?.trim().slice(0, 120) || null;
  return {
    displayName: normalizedDisplayName(fullName, email),
    email,
    fullName,
    provider: "google",
  };
}

export function authenticatedUser(input: {
  email: string;
  fullName?: string | null;
  provider: IdentityProvider;
}): AuthenticatedUser | null {
  const email = normalizeIdentityEmail(input.email);
  if (!isValidIdentityEmail(email)) return null;
  const fullName = input.fullName?.trim().replace(/\s+/g, " ").slice(0, 120) || null;
  return {
    displayName: normalizedDisplayName(fullName, email),
    email,
    fullName,
    provider: input.provider,
  };
}
